import { useEffect, useMemo, useRef, useState } from "react";
import "./EditarEmail.css";

const GROUP_OPTIONS = [
  "CCO",
  "PPCO",
  "UP Centro",
  "UP Norte",
  "UP Centro Norte",
  "UP Portos",
  "UP RS",
  "UP Serras",
  "Gerências",
  "Teste",
];

const ATIVO_OPTIONS = [
  { value: 1, label: "SIM" },
  { value: 0, label: "NÃO" },
];

function normalizeGroups(value) {
  const raw = Array.isArray(value) ? value : String(value || "").split(",");
  const out = [];
  const seen = new Set();

  for (const item of raw) {
    const name = String(item || "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }

  return out;
}

function orderGroups(groups = [], options = GROUP_OPTIONS) {
  return [...groups].sort((a, b) => {
    const ai = options.indexOf(a);
    const bi = options.indexOf(b);
    const av = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
    const bv = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
    if (av !== bv) return av - bv;
    return String(a).localeCompare(String(b));
  });
}

function EditarEmailDropdown({ label, name, value, options, onSelect, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeight = 44;

  const current = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onWindowChange = () => updatePlacement();
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".emed-box");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    const target = Math.min(320, options.length * itemHeight + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  return (
    <div
      ref={wrapRef}
      className={`emed-field emed-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}
    >
      <label>{label}</label>
      <div className="emed-pick-anchor">
        <button type="button" className="emed-pick-btn" aria-haspopup="listbox" aria-expanded={open} onClick={() => (open ? closeMenu() : openMenu())}>
          <span className={`emed-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`emed-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className="emed-pick-opt"
                onClick={() => {
                  onSelect(name, opt.value);
                  closeMenu();
                }}
                role="option"
                aria-selected={String(opt.value) === String(value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function EditarEmail({ open, data, onClose, onConfirm }) {
  const [form, setForm] = useState(null);

  const onlyDigits = (s) => String(s || "").replace(/\D/g, "");
  const formatBRPhone = (s) => {
    const d = onlyDigits(s).slice(0, 11);
    if (!d) return "";
    if (d.length <= 2) return d;
    let out = d.slice(0, 2);
    if (d.length > 2) out += " " + d.slice(2, 3);
    if (d.length > 3) out += " " + d.slice(3, 7);
    if (d.length > 7) out += " - " + d.slice(7, 11);
    return out;
  };
  const isValidBRPhone = (s) => onlyDigits(s).length === 11;

  const selectableGroups = useMemo(() => {
    const current = normalizeGroups(form?.grupos || []);
    const extras = current.filter((g) => !GROUP_OPTIONS.includes(g));
    return [...GROUP_OPTIONS, ...extras];
  }, [form?.grupos]);

  function toggleGrupo(grupo) {
    setForm((s) => {
      const current = normalizeGroups(s?.grupos || []);
      const exists = current.includes(grupo);
      const next = exists ? current.filter((g) => g !== grupo) : [...current, grupo];
      return { ...s, grupos: orderGroups(next, selectableGroups) };
    });
  }

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open && data) {
      const initialGroups = normalizeGroups(
        Array.isArray(data.grupos) && data.grupos.length ? data.grupos : data.grupo
      );

      setForm({
        id: data.id,
        nome: data.nome || "",
        apelido: data.apelido || "",
        grupos: orderGroups(initialGroups),
        email: data.email || "",
        telefone: formatBRPhone(data.telefone || ""),
        observacao: data.observacao || "",
        ativo: Number(data.ativo) ? 1 : 0,
      });
    }
  }, [open, data]);

  const telefoneInvalido = useMemo(() => {
    if (!form?.telefone) return false;
    return !isValidBRPhone(form.telefone);
  }, [form?.telefone]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") onClose?.();
    if (e.key === "Enter" && form?.email && !telefoneInvalido) {
      onConfirm?.({
        ...form,
        grupo: form.grupos?.[0] || "",
        grupos: form.grupos || [],
      });
    }
  };

  if (!open || !form) return null;

  return (
    <div className="emed-bg" onKeyDown={onKeyDown}>
      <div className="emed-box" role="dialog" aria-modal="true" aria-label="Editar e-mail">
        <header className="emed-head">
          <h3 className="emed-title">Editar e-mail</h3>
          <button className="emed-x" type="button" onClick={onClose} aria-label="Fechar">
            x
          </button>
        </header>

        <div className="emed-body">
          <div className="emed-row">
            <div className="emed-field">
              <label>Nome</label>
              <input
                value={form.nome}
                onChange={(e) => setForm((s) => ({ ...s, nome: e.target.value }))}
                placeholder="Nome completo"
              />
            </div>

            <div className="emed-field">
              <label>Apelido</label>
              <input
                value={form.apelido}
                onChange={(e) => setForm((s) => ({ ...s, apelido: e.target.value }))}
                placeholder="Como será exibido"
              />
            </div>
          </div>

          <div className="emed-row">
            <div className="emed-field emed-grow">
              <label>Grupos (clique para marcar/desmarcar)</label>
              <div className="emed-groups-grid">
                {selectableGroups.map((grupo) => {
                  const selected = (form.grupos || []).includes(grupo);
                  return (
                    <button
                      key={grupo}
                      type="button"
                      className={`emed-group-chip ${selected ? "is-on" : ""}`}
                      onClick={() => toggleGrupo(grupo)}
                    >
                      {grupo}
                    </button>
                  );
                })}
              </div>
              <div className="emed-hint">{(form.grupos || []).length} grupo(s) selecionado(s)</div>
            </div>
          </div>

          <div className="emed-row">
            <div className="emed-field">
              <label>E-mail</label>
              <input
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                placeholder="nome@empresa.com.br"
              />
            </div>

            <div className="emed-field">
              <label>Telefone</label>
              <input
                inputMode="numeric"
                value={form.telefone}
                onChange={(e) => setForm((s) => ({ ...s, telefone: formatBRPhone(e.target.value) }))}
                placeholder="41 9 9999 - 9999"
              />
              {telefoneInvalido && (
                <div className="emed-hint emed-err">
                  Informe no formato: <strong>41 9 9999 - 9999</strong>
                </div>
              )}
            </div>

            <EditarEmailDropdown
              label="Ativo"
              name="ativo"
              value={form.ativo}
              options={ATIVO_OPTIONS}
              onSelect={(name, nextValue) => setForm((s) => ({ ...s, [name]: Number(nextValue) }))}
              placeholder="SELECIONE"
            />
          </div>

          <div className="emed-row">
            <div className="emed-field emed-grow">
              <label>Observação</label>
              <textarea
                rows={3}
                value={form.observacao}
                onChange={(e) => setForm((s) => ({ ...s, observacao: e.target.value }))}
                placeholder="Notas, área, turno, etc."
              />
            </div>
          </div>
        </div>

        <footer className="emed-foot">
          <button className="emed-btn emed-btn--ghost" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="emed-btn emed-btn--primary"
            type="button"
            onClick={() =>
              onConfirm({
                ...form,
                grupo: form.grupos?.[0] || "",
                grupos: form.grupos || [],
              })
            }
            disabled={!form.email || telefoneInvalido}
          >
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}
